import {useState, useEffect, useCallback} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Container from '../../components/common/layout/Container';
import {Button, ButtonWithLoader} from '../common/Button';
import ActionButtons from '../common/layout/ActionButtons';
import Error from '../common/Error';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import EnsurePermission from '../common/EnsurePermission';
import Spinner from '../common/Spinner';
import {
  OptionsProp,
  StartProcessProps} from '../../constants/types';
import {startProcess} from '../../api/processes';
import permissionsInfo from '../../constants/permissionsInfo';
import {getAllPatients} from '../../api/patients';
import {startProcessValidationSchema} from '../../constants/validationSchemes';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import SuccessPopup from '../common/popup/SuccessPopup';
import CheckboxField from '../common/formik/fields/CheckboxField';
import processesRoutes from '../processes/routes';
import tasksRoutes from '../tasks/routes';
import patientsRoutes from '../patients/routes';
import {DEFAULT_ERROR} from '../../api/constants';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../hooks/useSnackbar';
import {AutocompleteField} from '../common/formik/fields';

const redirectFromSelect = {
  path: patientsRoutes.create(),
  from: processesRoutes.start()
};

const type = 'SCHEDULING';

const query = {
  direction: 'asc',
  page: '1',
  per_page: '1000000'
};

type LocationState = {
  newPatientId?: Nullable<number>
}

const StartProcess = () => {
  const {t} = useTranslation('processes');
  const history = useHistory();
  const {state: {newPatientId} = {newPatientId: null}} =
    useLocation<LocationState>();
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sortDirection = query.direction || 'asc';
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string[]>>>(null);
  const [getPatientsData, setPatientsData] = useState<any>([]);
  const [patientsOptions, setPatientsOptions] = useState<OptionsProp[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const {addErrorToast} = useSnackbarHook();

  const popupLabels = {
    body: t('startProcess.popup.bodyText', {processType: t(`processNames.${type}`)})
  };

  const popupConfirmableAction = () => history.push(tasksRoutes.tasks());

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await getAllPatients({...query, direction: sortDirection});
      setPatientsData(response);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();

    return () => {
      setError('');
    };
  }, [getAllPatients]);

  useEffect(() => {
    if ('content' in getPatientsData) {
      setPatientsOptions(
          getPatientsData.content.map(({id, firstName, lastName}) => ({
            value: id,
            label: `${firstName} ${lastName} (${id})`
          })));
    }
  }, [getPatientsData]);

  const onSubmit = async (values: StartProcessProps) => {
    const sanitizeValues = sanitize(values);
    setApiValidationError(null);

    try {
      await startProcess(type, sanitizeValues);
      setIsPopupOpen(true);
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'user');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const cancelCreation = () => {
    history.push(processesRoutes.list());
  };

  const initialValues = {
    patientId: newPatientId || null,
    wantsSooner: false
  };

  return (
    <Container header={t('startProcess.pageTitle')}>
      <h2 className="subheader">{t('startProcess.pageSubheader')}</h2>

      {isLoading ? (
          <Spinner loadingText={t('startProcess.spinnerLoadingText')} />
        ) : (
        <Formik
          initialValues={initialValues}
          validateOnBlur={false}
          onSubmit={onSubmit}
          validationSchema={startProcessValidationSchema}
          enableReinitialize
        >
          {({
            isSubmitting,
            isValid,
            dirty,
            submitForm
          }) => {
            useOnUnsavedChanges(dirty);

            return (
              <>
                <div className="creation-form-wrapper start-process">
                  <Form className="form">
                    <AutocompleteField
                      name="patientId"
                      label={t('startProcess.fields.patientIdLabel')}
                      withNewEntity={{
                        label: t('startProcess.fields.createNewPatientLabel'),
                        redirectOptions: redirectFromSelect,
                        permission: permissionsInfo.patients.create
                      }}
                      options={patientsOptions}
                      disabled={isSubmitting}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setError('')}
                      apiValidationError={apiValidationError}
                    />

                    <CheckboxField
                      name="wantsSooner"
                      label={t('startProcess.fields.wantsSoonerLabel')}
                      disabled={isSubmitting}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setError('')}
                    />
                  </Form>
                </div>

                <ActionButtons>
                  <EnsurePermission
                    elementPermissions={permissionsInfo.processes.start}>
                    <ButtonWithLoader
                      testId="create-group"
                      label={t('startProcess.buttons.startBtnLabel')}
                      disabled={!isValid}
                      isSubmitting={isSubmitting}
                      onClick={submitForm}
                      type="submit"
                    />
                  </EnsurePermission>

                  <Button
                    onClick={cancelCreation}
                    label={t('startProcess.buttons.cancelBtnLabel')}
                    variant="outlined"
                    color="secondary"
                  />
                </ActionButtons>
                <SuccessPopup
                  labels={popupLabels}
                  isOpen={isPopupOpen}
                  setIsOpen={setIsPopupOpen}
                  confirmableAction={popupConfirmableAction} />
              </>
            );
          }}
        </Formik>
      )}

      <Error message={error} />
    </Container>
  );
};

export default StartProcess;
